import { Alert, Box, Card, Stack, Typography } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import SplitButton, { ClickButton } from "../../../lib/SplitButton";
import StepperFormWrapper from "../../../lib/StepperFormWrapper";
import { StepperType } from "../../../lib/StepperWrapper";
import { ScreenTitle } from "../../../lib/typography";
import { currentMembership, useCurrentVisitor } from "../../../SessionBoundary";
import { MeetingItemData } from "../types";
import { WorkflowAction } from "../useWorkflowSteps";
import MeetingItemCard from "./MeetingItemCard";

function normalizeMeetingItemName(name?: string | null): string | undefined {
  return name?.trim().toLowerCase() || undefined;
}

type Props = {
  stepper: StepperType;
  action: WorkflowAction;
  defaultValues?: MeetingItemData[];
  onSubmit: (data: MeetingItemData[]) => void;
};

export default function SetMeetingItemsStep({
  stepper,
  defaultValues,
  action,
  onSubmit,
}: Props) {
  const isEdit = action === "edit";
  // orgMember is alias for admin or member
  const orgMember = useCurrentVisitor();
  const membership = currentMembership(orgMember);
  // only active org members should ever access this step
  if (!membership) throw new Error("missing a valid membership");

  const { t } = useTranslation();

  const [meetingItems, setMeetingItems] = useState<
    {
      // stable React key for list reconciliation
      id: string;
      data: MeetingItemData | null;
      isEditing: boolean;
    }[]
  >(
    defaultValues?.map((data) => ({ id: uuid(), data, isEditing: false })) ?? []
  );

  const isAnyEditing = meetingItems.some((item) => item.isEditing);

  const setEditing = (id: string | null) => {
    setMeetingItems((prev) =>
      prev.map((item) => ({ ...item, isEditing: item.id === id }))
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    onSubmit(
      meetingItems
        .map((entry) => entry.data)
        .filter((data): data is MeetingItemData => data !== null)
    );
  };

  // ensure the most recently added item is fully visible
  const scrollHelper = useRef<HTMLDivElement>(null);
  const scrollToBottom = () =>
    requestAnimationFrame(() =>
      scrollHelper.current?.scrollIntoView({ behavior: "smooth", block: "end" })
    );

  const addNewEmptyItem = () => {
    const id = uuid();
    setMeetingItems((prev) => [
      ...prev.map((item) => ({ ...item, isEditing: false })),
      { id, data: null, isEditing: true },
    ]);
    scrollToBottom();
  };

  const presetButton = (name: string): ClickButton => {
    return {
      label: name,
      onClick: () => addPresetItem(name),
      disabled: meetingItems.some(
        (item) =>
          normalizeMeetingItemName(item.data?.name) ===
          normalizeMeetingItemName(name)
      ),
    };
  };

  const addPresetItem = (itemName: string) => {
    setMeetingItems((prev) => [
      ...prev,
      { id: uuid(), data: { name: itemName }, isEditing: false },
    ]);
    scrollToBottom();
  };

  const removeItem = (id: string) => {
    setMeetingItems((prev) => prev.filter((item) => item.id !== id));
  };

  const savedNamesExceptCurrent = (id: string) => {
    return meetingItems
      .filter((item) => item.id !== id)
      .map((item) => normalizeMeetingItemName(item.data?.name))
      .filter((name): name is string => !!name);
  };

  const saveItem = (id: string, data: MeetingItemData) => {
    setMeetingItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, data, isEditing: false } : item
      )
    );
  };

  const hasNonNullMeetingItem = useMemo(
    () => meetingItems?.some((item) => item.data !== null) ?? false,
    [meetingItems]
  );

  return (
    <StepperFormWrapper
      stepper={stepper({
        disabled: isAnyEditing,
        loading: false,
        showSkip: !hasNonNullMeetingItem,
      })}
      handleSubmit={handleSubmit}
    >
      <ScreenTitle>{t("Declare items for security")}</ScreenTitle>
      <Typography
        variant="body1"
        sx={{
          color: "text.primary",
          mt: 2,
        }}
      >
        {t(
          "If you need to bring items through security, please list them for review and approval. For any other needs, please contact the facility."
        )}{" "}
      </Typography>

      {isEdit && (
        <Box
          sx={{
            my: 3,
          }}
        >
          <Alert severity="warning">
            {t(
              "Modifications to any items will require reapproval from the facility."
            )}
          </Alert>
        </Box>
      )}
      <Stack
        spacing={2}
        sx={{
          my: 6,
          flex: 1,
          minHeight: 0,
        }}
      >
        {meetingItems.length ? (
          <Stack
            spacing={2}
            sx={{
              flex: 1,
            }}
          >
            {meetingItems.map((entry) => (
              <MeetingItemCard
                key={entry.id}
                item={entry.data}
                isEditing={entry.isEditing}
                actionsEnabled={!isAnyEditing}
                existingNames={savedNamesExceptCurrent(entry.id)}
                onEdit={() => setEditing(entry.id)}
                onCancel={() => setEditing(null)}
                onSave={(data) => saveItem(entry.id, data)}
                onRemove={() => removeItem(entry.id)}
              />
            ))}
          </Stack>
        ) : (
          <Card variant="outlined" sx={{ p: 2, textAlign: "center" }}>
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
              }}
            >
              {t("No items yet")}
            </Typography>
          </Card>
        )}
        <SplitButton
          id="schedule-event-add-meeting-item"
          variant="outlined"
          disabled={isAnyEditing}
          sx={{ alignSelf: "end" }}
          buttons={[
            {
              label: t("Add item"),
              onClick: () => addNewEmptyItem(),
            },
            presetButton(t("Phone")),
            presetButton(t("Laptop")),
            presetButton(t("Records")),
            presetButton(t("Camera")),
            presetButton(t("Documents")),
            presetButton(t("Pen")),
          ]}
        />
      </Stack>
      <div ref={scrollHelper} />
    </StepperFormWrapper>
  );
}
